import type { TimeInterval, TimeType } from '@/types';
import {
  ROUTE_NAME_BOOKING_FORM_DATED,
  ROUTE_NAME_BOOKING_FORM_TEMPLATE,
  ROUTE_NAME_BOOKING_VIEW,
  ROUTE_NAME_CUSTOMER_HOME_VIEW,
  ROUTE_NAME_CUSTOMER_TRIPS_LIST,
  ROUTE_NAME_CUSTOMER_VEHICLE_TRACKING,
} from '@/config/routeNames';

export * from './customerPage.config';
export * from './contextualHelp.config';
export * from './transitOrganization.config';

/** Sélecteur du point d'entrée pour monter l'application Vue.js */
export const APP_MOUNT_POINT_SELECTOR = '#app';

export const SCREEN_SMALL = 320;

export const SCREEN_MEDIUM = 880;

/**
 * Pour l'accessibilité, une résolution d'écran de 1280x1024 est considéré comme résolution de base.
 * La hauteur à 200% est donc de 1024/2 = 512
 */
export const A11Y_SCREEN_SIZE_200PCT_ZOOM = 512;

/** Durée en heures de la validité du courriel envoyé.  Pour affichage uniquement.  */
export const EMAIL_VALIDITY_HOURS = 72;

/** Nom unique du local storage pour les informations de l'utilisateur */
export const LS_USER_CONFIG_KEY = 'monta-user-config';

/** ID utilisé sur le <main> du site et utilisé pas la navigation d'accessibilité pour passer au contenu */
export const A11Y_MAIN_CONTENT_ID = 'main-content';

export const LOCALE_COOKIE_NAME = 'mta-c';

/**
 * Délai d'attente en millisecondes avant que NVDA se synchronise avec le DOM.  Autrement,
 * Vue peut apporter plusieurs changements successifs dans le DOM mais NVDA captera seulement le premier changement et
 * non pas le dernier mis à jour, ce qui crée alors des problèmes de lecture (exemple, un élément non lu).
 */
export const A11Y_NVDA_SYNC_DELAY_MS = 100;

/** Délai par défaut avant l'affichage du texte dans le panneau de chargement */
export const LOADING_TEXT_VISIBILITY_DELAY_MS = 2000;

/** Délai par défaut avant d'annoncer le chargement aux lecteurs d'écran */
export const LOADING_TEXT_ANNOUNCER_DELAY_MS = 750;

/** Intervalle de récupération des données du profil de l'utilisateur, exprimé en secondes */
export const API_USER_PROFILE_REFETCH_INTERVAL_SECONDS = 60;

/** Intervalle de récupération des données temps réel d'un véhicule et/ou déplacement, exprimé en secondes */

export const API_TRIP_SERVICE_INFO_REFETCH_INTERVAL_SECONDS = 15;

/** Intervalle de récupération de la configuration systèmes (incluant les alertes) */
export const API_SYSTEM_SETTINGS_REFETCH_INTERVAL_SECONDS = 30;

/**
 * Délai maximal avant le retrait de la notification de la pile destiné à alimenter les notifications au lecteur
 * d'écran pour les informations temps réel dans la page d'accueil.
 */
export const REALTIME_ARIA_NOTIFICATION_TIMEOUT_MS = 5000;
/**
 * Délai maximal de conservation des données stockées dans le stockage de session,
 * durant le rechargement du fureteur par l'utilisateur, avant qu'elles soient détruites et que l'utilisateur doive
 * recommencer à la première étape du formulaire de réservation.
 */
export const BOOKING_FORM_DATA_PERSISTENCE_DELAY_MS = 15000;

/**
 * Délai de conservation des données de la réponse reçue pour une demande de réservation.  À l'intéreur de ce délai,
 * l'utilisateur qui recharge sa page retournera automatiquement sur le panneau de confirmation ou d'erreur.  Une fois
 * le délai dépassé, le formulaire est réinitialisé.
 *
 * Il s'agit d'un workaround à la limitation de Webkit qui ne supporte pas l'événement "beforeunload" :
 *  Cette gestion est implémenté dans les formulaires de réservation pour conserver les données lorsque
 * l'utilisateur recharge sa page mais ça ne fonctionne pas sur des appareils Apple/Webkit.
 */
export const SUBMISSION_RESPONSE_PERSISTENCE_DELAY_MS = 60000;

export const MONTA_BROADCAST_FILE_NUMBER = 'monta-bc-fileNumber';

/** Plage d'heure par défaut pour la réservation d'une demande de déplacement */
export const BOOKING_HOURS_DEFAULT: TimeInterval = {
  lowerBound: '06:00',
  upperBound: '00:30',
};

/** Heure maximale pour effectuer une réservation pour le lendemain */
export const BOOKING_RESERVATION_CUTOFF_TIME = '21:00';

/** Format par défaut des dates envoyées au backend lors d'une requête.
 * @see https://day.js.org/docs/en/parse/string-format
 */
export const BFF_DATE_FORMAT_NORMALIZED = 'YYYY-MM-DD';

/**
 * Nombre maximum de déplacements demandé pour l'affichage de l'historique des déplacements.
 */
export const HISTORY_TRIPS_MAX = 20;
/** Période maximale en années auquel l'historique des déplacements peut être retrouvé (limite GIRO) */
export const HISTORY_TRIPS_PERIOD_CUSTOM_SELECT_MINIMUM_YEAR = 2;
/** Date de début de la disponibilité de l'historique des déplacements pour les clients STL */
export const HISTORY_TRIPS_PERIOD_CUSTOM_SELECT_STL_START_DATE = '2026-01-01';

/**
 * Nombre minimal d'erreurs nécessaire afin que le panneau de statut s'affiche dans le haut du formulaire.
 *  S'applique uniquement aux formulaires utilisant cette fonctionnalité via `useFormErrorHandler` en combinaison avec
 * la composante `<ElFormWrapper>`.
 */
export const FORM_STATUS_MIN_ERRORS_BEFORE_RENDER = 2;

/** Le nom du paramètre d'uri possédant l'url de retour lors d'une connexion à l'application Mon Transport Adapté */
export const URI_RETURN_QUERY_PARAM_NAME = 'retour';
/** Le nom du paramètre d'uri possédant token d'identification lors de la gestion du mot de passe */
export const URI_TOKEN_QUERY_PARAM_NAME = 'jeton';
/**
 * Le nom du paramètre d'uri possédant le type d'action sur le compte lors de la creation ou la récupération d'un
 * compte
 */
export const URI_ACCOUNT_CONFIRMATION_TYPE_QUERY_PARAM_NAME = 'type';
/**
 * Le nom du paramètre d'uri possédant le courriel associé au compte lors de la creation ou la récupération d'un compte
 */
export const URI_ACCOUNT_CONFIRMATION_EMAIL_QUERY_PARAM_NAME = 'courriel';
/**
 * Le nom du paramètre d'uri possédant le numéro de dossier associé au compte lors de la creation ou la récupération
 * d'un compte
 */
export const URI_ACCOUNT_CONFIRMATION_FILE_NUMBER_QUERY_PARAM_NAME = 'numero-dossier';

/** Liste de routes devant être exclues du fil d'ariane. */
export const BREADCRUMB_PAGE_EXCLUSIONS = [
  ROUTE_NAME_CUSTOMER_HOME_VIEW, // évite la duplication de l'accueil dans le breadcrumb, vu que la route de base est toujours évaluée et qu'elle redirige vers la vraie page d'accueil.
  ROUTE_NAME_BOOKING_VIEW, // sélecteur de formulaires
  ROUTE_NAME_BOOKING_FORM_DATED, // formulaire de demande de réservation occasionnelle
  ROUTE_NAME_BOOKING_FORM_TEMPLATE, // formulaire de demande de réservation régulière
  ROUTE_NAME_CUSTOMER_VEHICLE_TRACKING, // page suivre mon véhicule
  ROUTE_NAME_CUSTOMER_TRIPS_LIST, // route globale contenant les listes de déplacements et les pages de détails.
];

/**
 * Liste de route devant être exclus pour l'affichage des messages d'urgence.
 * Le message de maintenance reste visible
 */
export const EMERGENCY_PAGE_EXCLUSIONS = [
  ROUTE_NAME_BOOKING_FORM_DATED, // formulaire de demande de réservation occasionnelle
  ROUTE_NAME_BOOKING_FORM_TEMPLATE, // formulaire de demande de réservation régulière
];

/** Type d'heure définie pour le point d'origine d'un déplacement */
export const TRIP_BOOKING_TIME_TYPE_OUTWARD_DEFAULT: TimeType = 'arrival';
/** Type pour l'heure de retour dans un déplacement "aller-retour" */
export const TRIP_BOOKING_TIME_TYPE_INWARD_DEFAULT: TimeType = 'departure';

/** ID Google Tag Manager */
export const GTM_ID = 'GTM-T4S2M63Q';

/** Cookie pour les acteurs mon transport adapté (Impersonate) */
export const MTA_ACTOR_COOKIE = '__Host-mta-acteur';
